// The kiosk Account area (F-KIO-15; spec/12 §8): the player's own pages at the station — who they are, the time
// they hold, their wallet here, their recent orders and their kiosk settings.
//
// The kiosk keeps the station-bridge design (architecture decision, daily review #709): it never holds a player
// token. The station reads these device-authenticated and hands them to the kiosk over the WebView2 bridge
// (`account/state`, spec/10 §5.3). They are scoped by construction: the station names no player — the server reads
// the player of **this station's own active session**, in the station's organisation, so a station can never read
// another player's account, and once the session ends there is nothing to read (404).
//
//   GET  /v1/station/sessions/current/account                      the Account pages' data, in one read
//   POST /v1/station/sessions/current/passes/{holdingId}/pin        use this queued pass next (F-SES-22)
//
// What the kiosk cannot do here is deliberate (spec/07 §4.3): nothing on this surface changes a password, the phone,
// the email or two-factor, deletes the account or shows payment methods — the Profile page is read-only and points to
// the player app and web for those. Contact details leave the server masked (spec/20 §6).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { PassHoldingIdSchema, PlayerIdSchema, SaleIdSchema, WalletTransactionIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { MoneySchema, NonNegativeMoneySchema } from "../primitives/money.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { SaleStatusSchema } from "./commerce.js";
import { WalletTransactionKindSchema } from "./wallet.js";
const STATION_ACCOUNT_AUTH = 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5). The station names no player: the ' +
    'account is that of the player on **this station’s own active session**, in the station’s organisation.';
/** Who the player is, read-only at the kiosk (F-KIO-15). Contact details are masked; `null` when there are none. */
export const StationAccountProfileSchema = z
    .object({
    playerId: PlayerIdSchema,
    /** The name the player goes by (gamer tag, else their name); `null` when the profile carries none. */
    displayName: z.string().nullable(),
    /** The gamer tag, when the player has one. */
    username: z.string().nullable(),
    /** The phone number, masked ("+91 98•••••10"). */
    maskedPhone: z.string().nullable(),
    /** The email address, masked ("r•••@example.com"); `null` for a phone-only account. */
    maskedEmail: z.string().nullable(),
})
    .openapi('StationAccountProfile', { description: 'The session player’s profile, read-only and masked, for the kiosk (F-KIO-15).' });
/**
 * Where a holding stands for this session (F-KIO-15): `in_use` — funding play now (or already partly played);
 * `queued` — lined up on this session after it, and pinnable; `held` — on the player's account but not funding this
 * session (outside its hours, another class, expired or used).
 */
export const StationAccountHoldingPlaceSchema = z
    .enum(['in_use', 'queued', 'held'])
    .openapi('StationAccountHoldingPlace', { description: 'Where a pass holding stands for the running session (F-KIO-15).' });
/** One of the player's passes (spec/06 §3.2) as the Account's Time page shows it (F-KIO-15). */
export const StationAccountHoldingSchema = z
    .object({
    id: PassHoldingIdSchema,
    name: z.string(),
    /** `active` | `exhausted` | `expired` | `refunded` | `voided` (spec/06 §3.2). */
    status: z.enum(['active', 'exhausted', 'expired', 'refunded', 'voided']),
    grantedSeconds: z.int().min(0),
    remainingSeconds: z.int().min(0),
    purchasedAt: IsoDateTimeSchema,
    firstUsedAt: IsoDateTimeSchema.nullable(),
    expiresAt: IsoDateTimeSchema.nullable(),
    place: StationAccountHoldingPlaceSchema,
    /** Its position among the queued passes (1 = next), or `null` when it is not queued. */
    queuePosition: z.int().min(1).nullable(),
})
    .openapi('StationAccountHolding', { description: 'One of the session player’s passes, for the kiosk Account (F-KIO-15).' });
/** One line of the wallet statement at the kiosk (F-KIO-15, F-WAL-08): what moved and when. */
export const StationAccountStatementLineSchema = z
    .object({
    id: WalletTransactionIdSchema,
    kind: WalletTransactionKindSchema,
    occurredAt: IsoDateTimeSchema,
    cashAmount: MoneySchema,
    bonusAmount: MoneySchema,
    description: z.string(),
})
    .openapi('StationAccountStatementLine', { description: 'One wallet statement line for the kiosk Account (F-KIO-15).' });
/** The player's wallet at this organisation only (closed-loop, ADR-0010): cash, bonus, total and recent lines. */
export const StationAccountWalletSchema = z
    .object({
    cash: NonNegativeMoneySchema,
    bonus: NonNegativeMoneySchema,
    total: NonNegativeMoneySchema,
    /** The most recent lines, newest first (at most 20). */
    statement: z.array(StationAccountStatementLineSchema),
})
    .openapi('StationAccountWallet', { description: 'The session player’s wallet at this organisation, for the kiosk Account (F-KIO-15).' });
/** One of the player's recent orders at this venue (F-KIO-15): the sale as it stands. */
export const StationAccountOrderSchema = z
    .object({
    id: SaleIdSchema,
    /** The venue's order number for the business day, once it has one. */
    orderNumber: z.int().min(1).nullable(),
    status: SaleStatusSchema,
    /** A short list of what was bought, e.g. "Masala Maggi ×2, Night pack". */
    summary: z.string(),
    total: MoneySchema,
    /** What it still owes (zero once paid, and for a cancelled order). */
    outstanding: MoneySchema,
    placedAt: IsoDateTimeSchema,
    /** Placed during this session (from this PC or at the desk for it). */
    thisSession: z.boolean(),
})
    .openapi('StationAccountOrder', { description: 'One of the session player’s recent orders, for the kiosk Account (F-KIO-15).' });
/** The kiosk settings for this session (F-KIO-15): the venue's defaults the player may change for the session. */
export const StationAccountSettingsSchema = z
    .object({
    /** `kiosk.uiSounds` as the venue set it (default off); the player's toggle lasts for this session. */
    uiSounds: z.boolean(),
})
    .openapi('StationAccountSettings', { description: 'The kiosk settings for the running session (F-KIO-15).' });
/** The kiosk Account area's data (F-KIO-15, spec/12 §8) for the player on the station's own active session. */
export const StationAccountSchema = z
    .object({
    profile: StationAccountProfileSchema,
    /** Active holdings first (in use, then queued in order, then held), then the most recent used or expired ones. */
    holdings: z.array(StationAccountHoldingSchema),
    /** `null` when the organisation has not enabled its wallet (`wallet.enabled`). */
    wallet: StationAccountWalletSchema.nullable(),
    /** This session's orders and the player's recent ones at this venue, newest first (at most 10). */
    orders: z.array(StationAccountOrderSchema),
    settings: StationAccountSettingsSchema,
})
    .openapi('StationAccount', { description: 'The kiosk Account area for the player on a station’s session (F-KIO-15).' });
export const getStationAccountRoute = createRoute({
    method: 'get',
    path: '/v1/station/sessions/current/account',
    operationId: 'getStationAccount',
    tags: ['sessions', 'stations'],
    summary: 'The station reads its session player’s Account pages (F-KIO-15)',
    description: `${STATION_ACCOUNT_AUTH} Returns the profile (read-only, contacts masked), the player’s passes with where each ` +
        'stands for this session, the wallet at this organisation with its recent statement, recent orders and the kiosk ' +
        'settings. Answers `404` when the station has no active session with a player. Display data: nothing here changes ' +
        'a credential, and every amount is the server’s.',
    responses: {
        200: jsonResponse(StationAccountSchema, 'The session player’s Account.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('This station has no active session with a player.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
const StationHoldingParamsSchema = z.object({ holdingId: PassHoldingIdSchema }).openapi('StationHoldingParams');
export const pinStationSessionPassRoute = createRoute({
    method: 'post',
    path: '/v1/station/sessions/current/passes/{holdingId}/pin',
    operationId: 'pinStationSessionPass',
    tags: ['sessions', 'passes', 'stations'],
    summary: 'Use a queued pass next on the station’s session (F-KIO-15, F-SES-22)',
    description: `${STATION_ACCOUNT_AUTH} Moves a pass queued on the running session to the front of the queue, so it funds play ` +
        'as soon as the time in use runs out; the funding plan is rebuilt and the new lease is pushed to the station, so the ' +
        'session bar shows the new order. A pass already in use, played through or not on this session is refused. ' +
        'Pinning the pass already next changes nothing. A pass valid only at another venue of the organisation is refused ' +
        'with `pass-not-valid-here` (spec/06 §3.3). Audited.',
    request: { params: StationHoldingParamsSchema },
    responses: {
        200: jsonResponse(StationAccountSchema, 'The Account, with the queue in its new order.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('This station has no active session with a player, or the player holds no such pass.', 'not-found'),
        409: problemResponse('The pass is not queued on this session, or is valid only at another venue.', 'pass-not-queued', 'pass-not-valid-here', 'session-already-ended'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=station-account.js.map